import { queryClient } from "./queryClient";

/**
 * Invalidates indicator comment caches for both page and version level
 * This ensures the comment section stays in sync with canvas changes
 */
export function invalidateIndicatorCommentCaches(
  pageId: string,
  versionId?: string
) {
  // Invalidate single page comments query
  queryClient.invalidateQueries({ 
    queryKey: ["/api/pages", pageId, "indicators-with-comments"] 
  });

  // Invalidate all pages comments query (only if versionId is provided and not empty)
  if (versionId) {
    queryClient.invalidateQueries({ 
      queryKey: ["/api/versions", versionId, "indicators-with-comments"] 
    });
  }
}

/**
 * Invalidates both indicator-specific cache and document pages cache
 * to ensure filmstrip thumbnails and main view stay in sync
 * Now also invalidates comment caches for full synchronization
 */
export function invalidateIndicatorCaches(
  pageId: string,
  documentId: string,
  versionNumber: number,
  versionId?: string
) {
  // Invalidate specific page indicators (for main viewing area)
  queryClient.invalidateQueries({ 
    queryKey: ["/api/pages", pageId, "indicators"] 
  });

  // Invalidate document pages with indicator counts (for filmstrip thumbnails)
  queryClient.invalidateQueries({ 
    queryKey: ["/api/documents", documentId, "versions", versionNumber.toString(), "pages"] 
  });

  // Always invalidate comment caches (helper internally handles missing versionId)
  invalidateIndicatorCommentCaches(pageId, versionId);
}

/**
 * Extended invalidation for operations that affect multiple pages
 * (for future use if needed)
 */
export function invalidateAllIndicatorCaches(
  documentId: string,
  versionNumber?: number
) {
  // Invalidate all page indicators
  queryClient.invalidateQueries({ 
    queryKey: ["/api/pages"], 
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey[0] === "/api/pages" && queryKey[2] === "indicators";
    }
  });

  // Invalidate document pages with indicator counts
  if (versionNumber) {
    queryClient.invalidateQueries({ 
      queryKey: ["/api/documents", documentId, "versions", versionNumber.toString(), "pages"] 
    });
  } else {
    // Invalidate all versions for this document
    queryClient.invalidateQueries({ 
      queryKey: ["/api/documents", documentId], 
      predicate: (query) => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === "/api/documents" && 
               queryKey[1] === documentId && 
               queryKey[2] === "versions" && 
               queryKey[4] === "pages";
      }
    });
  }
}